import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/api-client';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import {
  useRegenerateActivationCode,
  useRevokeTerminal,
  useSyncLagBulk,
  useTerminals,
  useUpdateTerminal,
  type TerminalKind,
  type SyncLagLevel,
  type SyncLagSnapshot,
  type Terminal,
  type TerminalStatus,
} from '@/hooks/use-terminals';
import { useLocations } from '@/hooks/use-locations';
import { useT } from '@/i18n/use-t';

/** VL-266 — mirrors `PosExternalNumberPattern` on the server. */
const POS_EXTERNAL_NUMBER = /^[0-9]{3,5}$/;

/** The shape `useT` returns, for the helpers below that cannot call a hook. */
type Translate = (key: string, bakedEnglish?: string) => string;

export function TerminalsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<TerminalStatus | 'any'>('any');
  const connectivity = searchParams.get('online');
  const online = connectivity === 'true' ? true : connectivity === 'false' ? false : undefined;
  const rawKind = searchParams.get('kind');
  const kind: TerminalKind | undefined =
    rawKind === 'pos' || rawKind === 'sco' || rawKind === 'attendant' ? rawKind : undefined;
  const setOnline = (value: 'any' | 'true' | 'false'): void => {
    const next = new URLSearchParams(searchParams);
    if (value === 'any') next.delete('online');
    else next.set('online', value);
    setSearchParams(next);
  };
  const setKind = (value: TerminalKind | 'any'): void => {
    const next = new URLSearchParams(searchParams);
    if (value === 'any') next.delete('kind');
    else next.set('kind', value);
    setSearchParams(next);
  };
  const { data, isLoading } = useTerminals(status === 'any' ? undefined : status, online, kind);
  const { data: lagData } = useSyncLagBulk();
  const lagByTerminal = new Map<string, SyncLagSnapshot>(
    (lagData?.snapshots ?? []).map((s) => [s.terminalId, s]),
  );
  const revoke = useRevokeTerminal();
  const regen = useRegenerateActivationCode();
  const update = useUpdateTerminal();
  const locations = useLocations();
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<{ activationCode: string; expiresAt: string } | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  // Named `tr` rather than `t`: the row variable in the table below is already
  // `t`, and shadowing it here would silently translate the wrong thing.
  const tr = useT('admin_ui');
  const posExternalLabel = tr('terminal.posExternalNumber.column', 'POS external number');
  const posExternalHint = tr(
    'terminal.posExternalNumber.hint',
    "The register's own number in the back office — 3 to 5 digits. Leave it empty if the register has none.",
  );

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{tr('terminals.title', 'Terminals')}</h1>
          <p className="text-sm text-muted-foreground">
            {data === undefined
              ? '…'
              : tr('terminals.list.total', '{count} total').replace('{count}', String(data.total))}
          </p>
        </div>
        <div className="flex gap-2">
          <Select
            aria-label={tr('terminals.field.kind', 'Kind')}
            value={kind ?? 'any'}
            onChange={(event) => setKind(event.target.value as TerminalKind | 'any')}
            className="w-40"
          >
            <option value="any">{tr('terminals.list.kindAny', 'All kinds')}</option>
            <option value="pos">{tr('terminals.list.kindPos', 'POS')}</option>
            <option value="sco">{tr('terminals.list.kindSco', 'SCO')}</option>
            <option value="attendant">{tr('terminals.list.kindAttendant', 'Attendant')}</option>
          </Select>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as TerminalStatus | 'any')}
            className="w-40"
          >
            <option value="any">{tr('terminals.list.statusAny', 'All statuses')}</option>
            <option value="provisioned">
              {tr('terminals.list.statusProvisioned', 'Provisioned')}
            </option>
            <option value="active">{tr('terminals.list.statusActive', 'Active')}</option>
            <option value="revoked">{tr('terminals.list.statusRevoked', 'Revoked')}</option>
          </Select>
          <Select
            aria-label={tr('terminals.list.connectivity', 'Connectivity')}
            value={online === undefined ? 'any' : String(online)}
            onChange={(event) => setOnline(event.target.value as 'any' | 'true' | 'false')}
            className="w-40"
          >
            <option value="any">{tr('terminals.list.connectivityAny', 'Any connectivity')}</option>
            <option value="true">{tr('terminals.badge.online', 'Online')}</option>
            <option value="false">{tr('terminals.badge.offline', 'Offline')}</option>
          </Select>
          <Button variant="outline" onClick={() => setShowLogs((s) => !s)}>
            {showLogs
              ? tr('terminals.list.hideLogDownload', 'Hide log download')
              : tr('terminals.list.downloadLogs', 'Download logs')}
          </Button>
          {/* RL-449: Link devices are not tills and are listed on their own. */}
          <Button asChild variant="outline">
            <Link to="/terminals/link">{tr('terminals.link.title', 'Link devices')}</Link>
          </Button>
          <Button asChild>
            <Link to="/terminals/new">{tr('terminals.registerTerminal', 'Register terminal')}</Link>
          </Button>
        </div>
      </header>

      {showLogs && <DownloadLogsPanel terminals={data?.terminals ?? []} />}

      {code !== null && (
        <ActivationCodeAlert
          activationCode={code.activationCode}
          expiresAt={code.expiresAt}
          onClose={() => setCode(null)}
        />
      )}

      <div className="rounded-md border bg-background">
        <Table>
          <THead>
            <TR>
              <TH>{tr('terminals.field.code', 'Code')}</TH>
              <TH>{tr('terminals.field.name', 'Name')}</TH>
              <TH>{tr('terminals.field.kind', 'Kind')}</TH>
              <TH>{tr('terminals.field.profile', 'Profile')}</TH>
              <TH>{tr('terminals.field.location', 'Location')}</TH>
              <TH>{tr('terminals.list.colPosNr', 'POS №')}</TH>
              <TH>{tr('terminals.list.colGpPosId', 'GP POS id')}</TH>
              <TH>{posExternalLabel}</TH>
              <TH>{tr('terminals.field.status', 'Status')}</TH>
              <TH>{tr('terminals.list.colSync', 'Sync')}</TH>
              <TH>{tr('terminals.list.colLastHeartbeat', 'Last heartbeat')}</TH>
              <TH className="w-48" />
            </TR>
          </THead>
          <TBody>
            {isLoading && (
              <TR>
                <TD colSpan={12} className="py-6 text-center text-muted-foreground">
                  {tr('common.loading', 'Loading…')}
                </TD>
              </TR>
            )}
            {data?.terminals.length === 0 && (
              <TR>
                <TD colSpan={12} className="py-6 text-center text-muted-foreground">
                  {tr(
                    'terminals.list.empty',
                    'No terminals match. Click "Register terminal" to add one.',
                  )}
                </TD>
              </TR>
            )}
            {data?.terminals.map((t) => (
              <TR key={t.id}>
                <TD className="font-mono text-xs">
                  <Link className="underline-offset-2 hover:underline" to={`/terminals/${t.id}`}>
                    {t.code}
                  </Link>
                </TD>
                <TD className="font-medium">{t.displayName}</TD>
                <TD>
                  <Badge variant="outline">{t.kind}</Badge>
                </TD>
                <TD className="text-sm text-muted-foreground">{t.profile?.name ?? '—'}</TD>
                <TD>
                  <Select
                    aria-label={tr('terminals.list.locationAria', 'Location for {code}').replace(
                      '{code}',
                      t.code,
                    )}
                    className="min-w-44"
                    value={t.locationId ?? ''}
                    disabled={update.isPending && update.variables?.id === t.id}
                    onChange={(event) => {
                      setError(null);
                      update
                        .mutateAsync({
                          id: t.id,
                          body: {
                            locationId: event.target.value === '' ? null : event.target.value,
                          },
                        })
                        .catch((err: unknown) => setError(extractError(err, tr)));
                    }}
                  >
                    <option value="">{tr('terminals.unassigned', '— unassigned —')}</option>
                    {locations.data?.items.map((location) => (
                      <option
                        key={location.id}
                        value={location.id}
                        disabled={location.status === 'archived'}
                      >
                        {location.code} — {location.name}
                        {location.status === 'archived'
                          ? ` ${tr('terminals.list.archived', '(archived)')}`
                          : ''}
                      </option>
                    ))}
                  </Select>
                </TD>
                <TD>
                  {/*
                    YUKI-197: which register this is inside its own store —
                    GP's `POSNr`. Authorable here because it was settable
                    through the API and visible nowhere, so an operator could
                    write it blind and never read it back.

                    Committed on blur rather than per keystroke: every change
                    is a PATCH, and typing "12" would otherwise save 1 first.
                    Blank clears it — an unauthored store number is a real
                    state, and the export omits the field rather than guessing.
                  */}
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    aria-label={tr(
                      'terminals.list.posNrAria',
                      'POS number in store for {code}',
                    ).replace('{code}', t.code)}
                    className="w-20"
                    defaultValue={t.posNrInStore ?? ''}
                    disabled={update.isPending && update.variables?.id === t.id}
                    onBlur={(event) => {
                      const raw = event.target.value.trim();
                      const next = raw === '' ? null : Number(raw);
                      if (next !== null && (!Number.isInteger(next) || next < 1)) {
                        setError(
                          tr(
                            'terminals.list.posNrInvalid',
                            'POS number must be a whole number of 1 or more.',
                          ),
                        );
                        return;
                      }
                      if ((t.posNrInStore ?? null) === next) return;
                      setError(null);
                      update
                        .mutateAsync({ id: t.id, body: { posNrInStore: next } })
                        .catch((err: unknown) => setError(extractError(err, tr)));
                    }}
                  />
                </TD>
                <TD>
                  {/*
                    YUKI-239: the integer `POSId` the GP export sends for this
                    till. Authorable because GP's consumer reads the field as
                    an int and refuses the platform code the export sends by
                    default, so a till whose id nobody can set here is a till
                    whose receipts GP will not take.

                    Blank clears it and restores code export — the end state
                    once the legacy POSes are gone — so an empty box is a real
                    setting, not a missing one. Committed on blur for the same
                    reason as the column beside it: every change is a PATCH,
                    and typing "12" would otherwise save 1 first.
                  */}
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    aria-label={tr('terminals.list.gpPosIdAria', 'GP POS id for {code}').replace(
                      '{code}',
                      t.code,
                    )}
                    max={2147483647}
                    title={tr(
                      'terminals.list.gpPosIdHint',
                      'The integer POSId GP expects (1, 2, 3…). Leave empty to export the terminal code instead.',
                    )}
                    className="w-20"
                    defaultValue={t.gpPosIdAuthored ?? ''}
                    disabled={update.isPending && update.variables?.id === t.id}
                    onBlur={(event) => {
                      const raw = event.target.value.trim();
                      const next = raw === '' ? null : Number(raw);
                      if (
                        next !== null &&
                        (!Number.isInteger(next) || next < 1 || next > 2147483647)
                      ) {
                        setError(
                          tr(
                            'terminals.list.gpPosIdInvalid',
                            'GP POS id must be a whole number from 1 to 2147483647.',
                          ),
                        );
                        return;
                      }
                      if ((t.gpPosIdAuthored ?? null) === next) return;
                      setError(null);
                      update
                        .mutateAsync({ id: t.id, body: { gpPosIdAuthored: next } })
                        .catch((err: unknown) => setError(extractError(err, tr)));
                    }}
                  />
                </TD>
                <TD>
                  {/*
                    VL-266: the number the retailer's own back office knows this
                    register by, carried on receipts exported to Masterkasa.
                    Authored here and never imported — the customer settled on
                    2026-09-12 that Masterkasa does not own this value, so an
                    import must not overwrite what an operator typed.

                    A TEXT box, not `type="number"`. "0475" is a real register in
                    an estate that mixes 3- and 4-digit numbers, and a numeric
                    input drops the leading zero before it is ever sent — the
                    export would then carry 475, a different register.

                    Committed on blur for the same reason as the two columns
                    beside it: every change is a PATCH, and typing "475" would
                    otherwise save "4" first.
                  */}
                  <Input
                    inputMode="numeric"
                    aria-label={`${posExternalLabel} — ${t.code}`}
                    title={posExternalHint}
                    className="w-28"
                    defaultValue={t.posExternalNumber ?? ''}
                    disabled={update.isPending && update.variables?.id === t.id}
                    onBlur={(event) => {
                      const raw = event.target.value.trim();
                      const next = raw === '' ? null : raw;
                      if (next !== null && !POS_EXTERNAL_NUMBER.test(next)) {
                        setError(
                          tr(
                            'terminal.posExternalNumber.invalid',
                            'POS external number must be 3 to 5 digits.',
                          ),
                        );
                        return;
                      }
                      if ((t.posExternalNumber ?? null) === next) return;
                      setError(null);
                      update
                        .mutateAsync({ id: t.id, body: { posExternalNumber: next } })
                        .catch((err: unknown) => setError(extractError(err, tr)));
                    }}
                  />
                </TD>
                <TD>
                  <StatusBadge status={t.status} isOnline={t.isOnline === true} />
                </TD>
                <TD>
                  <SyncDot snapshot={lagByTerminal.get(t.id)} />
                  {t.kind === 'attendant' && <KitchenDot snapshot={lagByTerminal.get(t.id)} />}
                </TD>
                <TD className="text-xs text-muted-foreground">{relative(t.lastHeartbeatAt, tr)}</TD>
                <TD className="space-x-2 text-right">
                  {t.status !== 'revoked' && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={regen.isPending}
                        onClick={() => {
                          setError(null);
                          regen
                            .mutateAsync(t.id)
                            .then((c) => setCode(c))
                            .catch((err: unknown) => setError(extractError(err, tr)));
                        }}
                      >
                        {tr('terminals.list.newCode', 'New code')}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" disabled={revoke.isPending}>
                            {tr('terminals.list.revoke', 'Revoke')}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {tr(
                                'terminals.list.revokeTitle',
                                'Revoke terminal "{code}"?',
                              ).replace('{code}', t.code)}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {tr(
                                'terminals.list.revokeHelp',
                                "The terminal's token stops working immediately and it can no longer sync or heartbeat. This cannot be undone — the device must be re-provisioned with a new activation code to come back online.",
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{tr('common.cancel', 'Cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className={cn(buttonVariants({ variant: 'destructive' }))}
                              onClick={() => {
                                setError(null);
                                revoke
                                  .mutateAsync(t.id)
                                  .catch((err: unknown) => setError(extractError(err, tr)));
                              }}
                            >
                              {tr('terminals.list.revokeConfirm', 'Revoke terminal')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        <p className="border-t px-3 py-2 text-xs text-muted-foreground">
          <strong>{tr('terminals.list.colPosNr', 'POS №')}</strong>{' '}
          {tr(
            'terminals.list.footnotePosNr',
            "is which register this is inside its own store (GP's POSNr).",
          )}{' '}
          <strong>{tr('terminals.list.colGpPosId', 'GP POS id')}</strong>{' '}
          {tr(
            'terminals.list.footnoteGpPosId',
            'is the integer POSId GP expects for the till (1, 2, 3…) — leave it empty to export the terminal code instead.',
          )}{' '}
          <strong>{posExternalLabel}</strong> — {posExternalHint}
        </p>
      </div>

      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/** Default range: the last 24 hours, as datetime-local values. */
function defaultRange(): { from: string; to: string } {
  const toLocal = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const now = new Date();
  return { from: toLocal(new Date(now.getTime() - 24 * 60 * 60 * 1000)), to: toLocal(now) };
}

/**
 * Device-log range download (RFL-178): pick one or more terminals + a
 * date-time range, get a zip with one NDJSON file per terminal. Logs
 * appear once terminals ship the `sync.terminal-logs` uploader
 * (RFL-179) — until then files come back empty.
 */
function DownloadLogsPanel({ terminals }: { terminals: Terminal[] }) {
  // `tr`, not `t`: the terminal row below is already bound to `t`.
  const tr = useT('admin_ui');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [{ from, to }, setRange] = useState(defaultRange);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function download() {
    setError(null);
    setBusy(true);
    try {
      const res = await apiClient.get<Blob>('/terminal-logs/download', {
        params: {
          terminalIds: Array.from(selected).join(','),
          from: new Date(from).toISOString(),
          to: new Date(to).toISOString(),
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const disposition = (res.headers as Record<string, string>)['content-disposition'] ?? '';
      const match = /filename="([^"]+)"/.exec(disposition);
      a.download = match?.[1] ?? 'terminal-logs.zip';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractError(err, tr));
    } finally {
      setBusy(false);
    }
  }

  const valid = selected.size > 0 && from !== '' && to !== '' && new Date(from) <= new Date(to);

  return (
    <section className="space-y-4 rounded-md border bg-card p-5">
      <div>
        <h2 className="text-sm font-semibold">
          {tr('terminals.logsDownload.title', 'Download device logs')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {tr(
            'terminals.logsDownload.help',
            'Pick terminal(s) and a range — you get a zip with one log file (NDJSON) per terminal.',
          )}
        </p>
      </div>
      <div className="grid gap-4 md:grid-cols-[1fr_auto_auto_auto]">
        <div className="space-y-1">
          <Label>{tr('terminals.title', 'Terminals')}</Label>
          <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border p-2">
            {terminals.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {tr('terminals.logsDownload.none', 'No terminals.')}
              </p>
            )}
            {terminals.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-primary"
                  checked={selected.has(t.id)}
                  onChange={() => toggle(t.id)}
                />
                <span className="font-mono text-xs">{t.code}</span>
                <span className="text-muted-foreground">{t.displayName}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="logs-from">{tr('terminals.field.from', 'From')}</Label>
          <Input
            id="logs-from"
            type="datetime-local"
            value={from}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="logs-to">{tr('terminals.field.to', 'To')}</Label>
          <Input
            id="logs-to"
            type="datetime-local"
            value={to}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
          />
        </div>
        <div className="flex items-end">
          <Button disabled={!valid || busy} onClick={() => void download()}>
            {busy
              ? tr('terminals.logsDownload.preparing', 'Preparing…')
              : tr('terminals.logsDownload.submit', 'Download zip')}
          </Button>
        </div>
      </div>
      {error !== null && <p className="text-sm text-destructive">{error}</p>}
    </section>
  );
}

export function StatusBadge({ status, isOnline }: { status: TerminalStatus; isOnline: boolean }) {
  const t = useT('admin_ui');
  if (status === 'revoked')
    return <Badge variant="destructive">{t('terminals.badge.revoked', 'revoked')}</Badge>;
  if (status === 'provisioned')
    return <Badge variant="outline">{t('terminals.badge.provisioned', 'provisioned')}</Badge>;
  return (
    <Badge variant={isOnline ? 'success' : 'outline'}>
      {isOnline ? t('terminals.badge.online', 'online') : t('terminals.badge.offline', 'offline')}
    </Badge>
  );
}

export function relative(at: string | null, t: Translate): string {
  if (at === null) return '—';
  const ms = Date.now() - new Date(at).getTime();
  const ago = (key: string, en: string, n: number) => t(key, en).replace('{n}', String(n));
  if (ms < 60_000) return ago('terminals.relative.secondsAgo', '{n}s ago', Math.round(ms / 1000));
  if (ms < 3_600_000)
    return ago('terminals.relative.minutesAgo', '{n}m ago', Math.round(ms / 60_000));
  if (ms < 86_400_000)
    return ago('terminals.relative.hoursAgo', '{n}h ago', Math.round(ms / 3_600_000));
  return ago('terminals.relative.daysAgo', '{n}d ago', Math.round(ms / 86_400_000));
}

export function ActivationCodeAlert({
  activationCode,
  expiresAt,
  onClose,
}: {
  activationCode: string;
  expiresAt: string;
  onClose: () => void;
}) {
  const t = useT('admin_ui');
  return (
    <div className="rounded-md border-2 border-emerald-400 bg-emerald-50 p-5 dark:border-emerald-700 dark:bg-emerald-950/30">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium">
            {t('terminals.activation.title', 'Activation code (shown once)')}
          </p>
          <p className="font-mono text-xl tracking-wider">{activationCode}</p>
          <p className="text-xs text-muted-foreground">
            {t('terminals.activation.expires', 'Expires {when}').replace(
              '{when}',
              new Date(expiresAt).toLocaleString(),
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void navigator.clipboard.writeText(activationCode);
            }}
          >
            {t('terminals.activation.copy', 'Copy')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function extractError(err: unknown, t: Translate): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string } | undefined;
    return data?.message ?? err.message;
  }
  return t('terminals.error.unexpected', 'Unexpected error');
}

/**
 * One-cell sync-lag indicator. Hover surfaces a per-domain
 * breakdown — full detail page is in the terminal-detail view
 * (Sprint 4 ticket 4.5 ships the bulk dot; per-domain panel ships
 * with the operator-visibility polish in Sprint 12 per TRS §21.2).
 */
function SyncDot({ snapshot }: { snapshot?: SyncLagSnapshot }) {
  const t = useT('admin_ui');
  const level: SyncLagLevel = snapshot?.worstLevel ?? 'unknown';
  const levelLabel = {
    green: t('terminals.sync.levelGreen', 'green'),
    yellow: t('terminals.sync.levelYellow', 'yellow'),
    red: t('terminals.sync.levelRed', 'red'),
    unknown: t('terminals.sync.levelUnknown', 'unknown'),
  }[level];
  const colour =
    level === 'green'
      ? 'bg-emerald-500'
      : level === 'yellow'
        ? 'bg-amber-500'
        : level === 'red'
          ? 'bg-red-500'
          : 'bg-muted-foreground/30';
  const tooltip =
    snapshot === undefined
      ? t('common.loading', 'Loading…')
      : level === 'unknown'
        ? t('terminals.sync.noTelemetry', 'No telemetry yet')
        : t('terminals.sync.lastSync', '{level} — last sync {when}')
            .replace('{level}', levelLabel)
            .replace('{when}', relative(snapshot.reportedAt, t));
  return (
    <span className="inline-flex items-center gap-2" title={tooltip}>
      <span className={`inline-block h-2 w-2 rounded-full ${colour}`} aria-hidden="true" />
      <span className="text-xs text-muted-foreground">{levelLabel}</span>
    </span>
  );
}

/**
 * FN-92 — the kitchen station's own order path (broker consumer, outbox,
 * printer) as one dot beside the sync dot, only for attendant-kind rows.
 */
function KitchenDot({ snapshot }: { snapshot?: SyncLagSnapshot }) {
  const t = useT('admin_ui');
  const kitchen = snapshot?.kitchen;
  if (kitchen === undefined) return null;
  const colour =
    kitchen.level === 'green'
      ? 'bg-emerald-500'
      : kitchen.level === 'yellow'
        ? 'bg-amber-500'
        : kitchen.level === 'red'
          ? 'bg-red-500'
          : 'bg-muted-foreground/30';
  const tooltip =
    kitchen.reason ??
    t('terminals.kitchen.dotHealthy', 'broker consuming, outbox empty, printer ready');
  return (
    <span
      className="ml-3 inline-flex items-center gap-2"
      title={tooltip}
      data-testid="kitchen-dot"
      aria-label={t('terminals.kitchen.dotAria', 'Kitchen: {level}').replace(
        '{level}',
        kitchen.level,
      )}
    >
      <span className={`inline-block h-2 w-2 rounded-full ${colour}`} aria-hidden="true" />
      <span className="text-xs text-muted-foreground">
        {t('terminals.kitchen.dotLabel', 'kitchen')}
        {kitchen.broker.outboxDepth > 0
          ? ` · ${t('terminals.kitchen.dotQueued', '{count} queued').replace(
              '{count}',
              String(kitchen.broker.outboxDepth),
            )}`
          : ''}
      </span>
    </span>
  );
}
